/**
 * Matching a spoken name to a child on today's roster.
 *
 * A general-purpose recogniser is being asked to transcribe proper nouns it has never seen,
 * many of them romanised Mandarin, spoken over a playground. It will not produce the spelling
 * on file. "Ziyuan" comes back as "Zee you on", "Zhang" as "Jang", "Xiao" as "Shou".
 *
 * What makes that tractable is that the answer set is tiny and known: the ~30–65 children on
 * the roster in front of the teacher. So nothing here tries to spell a name correctly — it
 * scores every child on the roster against what was heard and reports how sure it is.
 *
 * Four independent routes to the same question, combined by weight rather than by preference,
 * because each one survives a different kind of damage:
 *
 *   1. **Phonetic** — letter distance plus a key tuned for the confusions this recogniser
 *      actually makes. Strong when the transcript is close to the spelling.
 *   2. **Syllable** — the transcript re-spelled as pinyin, cut into initials and finals, and
 *      aligned with a confusion-cost matrix. Strong when the transcript is a *different*
 *      romanisation of the right name: "chow way ming" for Zhou Weiming, where every letter
 *      is wrong and every sound is right.
 *   3. **Initials** — "lsy" for Li Si Yuan. Weak, but it survives a transcript so mangled
 *      that neither of the above finds anything.
 *   4. **Recency and learned aliases** — this teacher, this child, last Tuesday. The only
 *      signal here that gets better on its own.
 *
 * Nothing here decides anything. It ranks the roster and hands back the few children a name
 * could plausibly have been, and a teacher picks. That is the whole contract: recognition is
 * too unreliable to act on by itself, and a shortlist of five is something a person can read
 * at a glance and be sure about.
 *
 * Every layer is switchable through `MatchConfig` so the eval harness can say what each one
 * is worth. See `README.md` in this directory for the numbers.
 */

import { nameTokens } from '../../utils/studentPhotoName';
import { DEFAULT_CONFIG, type MatchConfig } from './matchConfig';
import { pinyinTokenSimilarity, nameInitials, initialsSimilarity, couldBePinyin } from './pinyin/score';
import { nameVariants } from './pinyin/hanReadings';
import { LETTER_NAME_SYLLABLES } from './pinyin/english';

/**
 * A roster row, reduced to what matching needs. `key` is whatever the caller wants back.
 *
 * Everything past `name` is optional and absent for most children today. A signal a child
 * has no data for is dropped from *both* halves of the weighted average rather than scored
 * as zero — otherwise a child with no English name on file is permanently penalised against
 * a child who has one, for a reason that has nothing to do with what the teacher said.
 */
export interface MatchableName {
  key: string;
  name: string;
  /**
   * 中文姓名, if it is ever collected. Dormant: 0 of 267 active students have one today.
   * See `pinyin/hanReadings.ts`.
   */
  chineseName?: string | null;
  /** An English or preferred given name distinct from the one in `name`. */
  preferredName?: string | null;
  /** Epoch ms when this teacher last picked this child by voice. Feeds the recency prior. */
  lastPickedAt?: number | null;
  /** Transcripts a teacher has corrected onto this child. An exact hit dominates. */
  aliases?: readonly string[] | null;
}

/** What each signal contributed, so the harness can attribute a win or a miss. */
export interface ScoreBreakdown {
  syllable: number;
  phonetic: number;
  initials: number;
  english: number;
  recency: number;
  /** True when a learned alias matched exactly and short-circuited the rest. */
  alias: boolean;
}

export interface NameScore {
  key: string;
  name: string;
  /** 0–1, where 1 is the name said exactly. */
  score: number;
  /** Present when the caller asked for it; the harness always does. */
  breakdown?: ScoreBreakdown;
}

/**
 * The most children ever offered at once.
 *
 * A shortlist is only useful while it can be read without reading it — five rows is about
 * the limit of that, and a name that fits more than five children on one roster is one the
 * teacher will have to find by hand anyway.
 */
export const MAX_SUGGESTIONS = 5;

/** Below this a child is not offered at all. A wrong name on the list is still noise. */
export const SUGGESTION_FLOOR = 0.45;

/**
 * How far behind the leader a child may be and still be worth offering.
 *
 * A flat floor alone makes bad lists in both directions: it pads a clear winner with four
 * names scoring half as much, and it truncates a genuine four-way tie. Measuring from the
 * best score instead keeps the list as long as the doubt actually is — "zee you on lee" has
 * one candidate near 0.80 and nothing else above 0.30, so it offers one.
 */
export const SUGGESTION_SPREAD = 0.25;

/**
 * A name reduced to how it *sounds*, roughly, to a recogniser working in English.
 *
 * Vowels after the first character are dropped, the way Soundex and Metaphone drop them:
 * they are the part a recogniser gets wrong first and they carry the least identity. The
 * consonant folds below are the ones that matter for this school's roster specifically —
 * the pinyin initials have no English equivalent, so the recogniser reaches for whichever
 * English spelling is nearest and picks a different one each time.
 */
/**
 * Keys already worked out.
 *
 * Every roster name is re-keyed against every spoken word, and the panel now re-scores on
 * each interim result — several times a second while a teacher is still talking. The keys
 * themselves are pure and there are only a few hundred distinct words in play, so computing
 * one twice is pure waste.
 */
const KEY_CACHE = new Map<string, string>();

/** Beyond this the cache is doing more harm than good; roster names are far fewer. */
const KEY_CACHE_LIMIT = 5_000;

export function phoneticKey(raw: string): string {
  const cached = KEY_CACHE.get(raw);
  if (cached !== undefined) return cached;
  const computed = computePhoneticKey(raw);
  if (KEY_CACHE.size >= KEY_CACHE_LIMIT) KEY_CACHE.clear();
  KEY_CACHE.set(raw, computed);
  return computed;
}

function computePhoneticKey(raw: string): string {
  const s = raw
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z]/g, '');
  if (s === '') return '';

  const out: string[] = [];
  let i = 0;
  const isVowel = (c: string) => 'aeiou'.includes(c);

  while (i < s.length) {
    const two = s.slice(i, i + 2);
    const three = s.slice(i, i + 3);

    // Digraphs first — every one of these is a single sound that its letters do not spell.
    if (three === 'sch') { out.push('S'); i += 3; continue; }
    if (two === 'zh' || two === 'dj') { out.push('J'); i += 2; continue; }  // Zhang / Jang
    if (two === 'ch' || two === 'ts' || two === 'tz') { out.push('C'); i += 2; continue; } // Chen / Tsai
    if (two === 'sh') { out.push('S'); i += 2; continue; }
    if (two === 'ph') { out.push('F'); i += 2; continue; }
    if (two === 'th') { out.push('T'); i += 2; continue; }
    if (two === 'ck' || two === 'kh') { out.push('K'); i += 2; continue; }
    if (two === 'gh') { out.push('G'); i += 2; continue; }
    if (two === 'kn' || two === 'gn') { out.push('N'); i += 2; continue; }
    if (two === 'wr') { out.push('R'); i += 2; continue; }
    // The single biggest source of near-misses on this roster: a recogniser cannot reliably
    // hear the difference between a Mandarin -n and -ng ending. Fold them together.
    if (two === 'ng') { out.push('N'); i += 2; continue; }

    const c = s[i];
    i += 1;

    if (isVowel(c)) {
      // Only a leading vowel survives — "Emma" and "Ema" must agree, "Anna" and "Ana" too.
      if (out.length === 0) out.push(c.toUpperCase());
      continue;
    }

    switch (c) {
      // Silent in the middle of a word, but the front of "Harper" is not the front of
      // "Arper" — and dropping it there was enough to stop Harper and Hopper rhyming.
      case 'h': if (out.length === 0) out.push('H'); break;
      // Both are vowels everywhere but the front: the -ew of "Jew" is the -u of "Zhu", and
      // the -y of "Lily" is its -ie.
      case 'y': case 'w': if (out.length === 0) out.push(c.toUpperCase()); break;
      case 'j': out.push('J'); break;
      case 'q': out.push('C'); break;        // pinyin q ≈ ch
      case 'x': out.push('S'); break;        // pinyin x ≈ sh
      case 'z': out.push('S'); break;        // z/s is a coin flip over background noise
      case 's': out.push('S'); break;
      case 'c': out.push('aeiy'.includes(s[i] ?? '') ? 'S' : 'K'); break;
      case 'k': out.push('K'); break;
      case 'g': out.push('G'); break;
      case 'v': out.push('F'); break;
      case 'f': out.push('F'); break;
      default: out.push(c.toUpperCase());
    }
  }

  // Doubled sounds are a spelling convention, not a sound: Lilly and Lily are one name.
  return out.filter((ch, idx) => ch !== out[idx - 1]).join('');
}

/** Classic Levenshtein, two rows. Names are short; this is never the expensive part. */
function editDistance(a: string, b: string): number {
  if (a === b) return 0;
  if (a.length === 0) return b.length;
  if (b.length === 0) return a.length;

  let prev = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i++) {
    const row = [i];
    for (let j = 1; j <= b.length; j++) {
      row[j] = Math.min(
        prev[j] + 1,
        row[j - 1] + 1,
        prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1),
      );
    }
    prev = row;
  }
  return prev[b.length];
}

/** How alike two single name-words are, 0–1. */
export function tokenSimilarity(spoken: string, actual: string): number {
  if (spoken === actual) return 1;
  if (spoken === '' || actual === '') return 0;

  const longest = Math.max(spoken.length, actual.length);
  const raw = 1 - editDistance(spoken, actual) / longest;
  let best = raw;

  // A truncated hearing: "Emm" for "Emma", "Xiao" for "Xiaoming". Only from three letters
  // up — below that half the roster is a prefix of the other half.
  const shortest = Math.min(spoken.length, actual.length);
  if (shortest >= 3 && (actual.startsWith(spoken) || spoken.startsWith(actual))) {
    best = Math.max(best, 0.92 - 0.04 * (longest - shortest));
  }

  // Sounds the same. Worth a floor, not a verdict — and the floor rises with how close the
  // spellings already were, so a rhyme scores lower than a near-miss.
  const key = phoneticKey(spoken);
  if (key !== '' && key === phoneticKey(actual)) {
    best = Math.max(best, 0.72 + 0.18 * raw);
  }

  return Math.min(1, Math.max(0, best));
}

/**
 * The best exclusive pairing of spoken words to a child's name-words.
 *
 * Strongest pairing first, each word usable once. Pairing exclusively is what stops a
 * stutter — "chen chen" — from scoring as a perfect two-word match against a single Chen.
 *
 * Unmatched words on the *child's* side cost nothing: "Emma" for "Emma Chen" is how a
 * teacher refers to a child they know, and it should score 1.0. That it may also score 1.0
 * for a second Emma is the point — resolving that is the caller's job, not this one's.
 */
type Similarity = (spoken: string, actual: string) => number;

function pairScore(
  spokenTokens: readonly string[],
  actualTokens: readonly string[],
  sim: Similarity,
): number {
  const pairs: { s: number; a: number; sim: number }[] = [];
  for (let s = 0; s < spokenTokens.length; s++) {
    for (let a = 0; a < actualTokens.length; a++) {
      pairs.push({ s, a, sim: sim(spokenTokens[s], actualTokens[a]) });
    }
  }
  pairs.sort((x, y) => y.sim - x.sim);

  const usedSpoken = new Set<number>();
  const usedActual = new Set<number>();
  let total = 0;
  for (const pair of pairs) {
    if (usedSpoken.has(pair.s) || usedActual.has(pair.a)) continue;
    usedSpoken.add(pair.s);
    usedActual.add(pair.a);
    total += pair.sim;
    if (usedSpoken.size === spokenTokens.length) break;
  }

  // Divided by what was *said*, so a word the roster cannot account for drags the score down.
  const strict = total / spokenTokens.length;

  // ...except that a teacher is not a dictation machine, and the stopword list will never
  // be complete. "Can you check out Lily Hsu thanks" leaves "can" and "you" unaccounted for
  // — one is not on the list and the other is deliberately kept off it, because "you" is a
  // fragment of half the romanised names on the roster. Under the strict reading alone the
  // right child can score at most two words out of four, and lands third.
  //
  // So a bounded number of unmatched spoken words may be forgiven at a fixed price each.
  // Being explicitly priced is what keeps this from becoming a licence to ignore evidence:
  // a genuinely unrelated utterance still has to pay for every word it cannot place, and
  // three forgiven words cost more than any pairing can earn back.
  const unmatched = spokenTokens.length - usedSpoken.size;
  const lenient = total / actualTokens.length - UNMATCHED_WORD_COST * unmatched;

  return Math.max(strict, lenient);
}

/** What one unaccounted-for spoken word costs. High enough that filler is not free. */
const UNMATCHED_WORD_COST = 0.12;

/** Every way of gluing `tokens` back together into exactly `blocks` runs of neighbours. */
function contiguousPartitions(tokens: readonly string[], blocks: number): string[][] {
  if (blocks === 1) return [[tokens.join('')]];
  if (blocks === tokens.length) return [[...tokens]];
  if (blocks > tokens.length) return [];

  const out: string[][] = [];
  // Where the first block ends. It must leave at least one word for every remaining block.
  for (let cut = 1; cut <= tokens.length - blocks + 1; cut++) {
    const head = tokens.slice(0, cut).join('');
    for (const rest of contiguousPartitions(tokens.slice(cut), blocks - 1)) {
      out.push([head, ...rest]);
    }
  }
  return out;
}

/**
 * Beyond this many spoken words, stop enumerating regroupings and take the plain reading.
 *
 * Partition counts grow quickly and a seven-word utterance is not one name anyway — it is a
 * teacher who said a whole sentence, where the words themselves carry the match.
 */
const MAX_PARTITIONED_TOKENS = 6;

/**
 * How well one spoken name fits one child.
 *
 * The complication is that word boundaries are something the recogniser *invents*. A name it
 * has never seen comes back chopped up — "Ziyuan" as "zee you on" — so comparing word for
 * word asks the roster to account for three words it only has one of, and the best possible
 * score for the right child collapses to a third.
 *
 * So the spoken words are re-glued every way the neighbours allow, and the best reading wins.
 * "zee you on lee" is scored as four words, as three, as two ("zeeyouon" + "lee", which is
 * the one that finds Ziyuan Li) and as one. Merging only ever joins *adjacent* words, so no
 * reading exists that the teacher did not say in that order.
 */
export function scoreName(
  spokenTokens: readonly string[],
  actualTokens: readonly string[],
  sim: Similarity = tokenSimilarity,
  reglue = true,
): number {
  if (spokenTokens.length === 0 || actualTokens.length === 0) return 0;

  let best = pairScore(spokenTokens, actualTokens, sim);

  // Both sides glued whole — the mirror case, where the recogniser ran a name together.
  best = Math.max(best, sim(spokenTokens.join(''), actualTokens.join('')));

  if (reglue && spokenTokens.length <= MAX_PARTITIONED_TOKENS) {
    // Inclusive of `maxBlocks`, which is the reading that matters most: the one with as
    // many blocks as the roster name has words. Stopping one short of it meant "jang shin
    // yee" was never regrouped as "jang" + "shinyee" — the only reading that finds Xinyi
    // Zhang — and the case survived only because gluing *both* sides whole happened to
    // rescue it. Names where that trick does not apply were quietly being missed.
    const maxBlocks = Math.min(spokenTokens.length, actualTokens.length);
    for (let blocks = 1; blocks <= maxBlocks; blocks++) {
      for (const grouped of contiguousPartitions(spokenTokens, blocks)) {
        best = Math.max(best, pairScore(grouped, actualTokens, sim));
      }
    }
  }

  return best;
}

/**
 * One transcript out of the recogniser's N-best list.
 *
 * The matcher takes a list rather than a string because the biggest available accuracy win
 * is not in this file at all — it is server-side ASR with the day's roster fed in as a phrase
 * bias, which returns several ranked hypotheses instead of one. BHCS-100 puts that in a
 * separate ticket, but the input contract is shaped for it now so that wiring it in later is
 * a change to the caller and nothing else.
 */
export interface TranscriptCandidate {
  transcript: string;
  /** The recogniser's own confidence, 0–1. Absent means "no opinion", scored as 1. */
  confidence?: number;
}

/** What the caller may hand in: one transcript, or the recogniser's ranked alternatives. */
export type SpokenInput = string | readonly (string | TranscriptCandidate)[];

function asCandidates(input: SpokenInput): TranscriptCandidate[] {
  if (typeof input === 'string') return [{ transcript: input, confidence: 1 }];
  return input.map(c => (typeof c === 'string' ? { transcript: c, confidence: 1 } : c));
}

/** Milliseconds in a day, for the recency decay. */
const DAY_MS = 86_400_000;

/**
 * How much a recent pick counts, decaying to nothing over the configured half-life.
 *
 * Deliberately gentle. A teacher checking the same child out every afternoon is real
 * evidence, but it must never be enough on its own to put the wrong child first — a sibling
 * with a similar name would then be permanently shadowed by whichever one was picked
 * yesterday. At weight 0.10 it breaks ties and does nothing else.
 */
function recencyScore(lastPickedAt: number | null | undefined, now: number, halfLifeDays: number): number | null {
  if (!lastPickedAt) return null;
  const ageDays = Math.max(0, (now - lastPickedAt) / DAY_MS);
  return Math.pow(0.5, ageDays / halfLifeDays);
}

/**
 * The roster names to score a child under.
 *
 * Normally one — the name as displayed. A child with a Chinese name on file also gets every
 * reading of it, which is where heteronym surnames earn their place: 乐 indexed as both
 * "yue" and "le" matches whichever the family actually says. Dormant today; no child has
 * the field.
 */
function scorableNames(entry: MatchableName, config: MatchConfig): string[][] {
  const forms = [nameTokens(entry.name)];
  if (config.layers.heteronym && entry.chineseName) {
    for (const variant of nameVariants(entry.chineseName)) forms.push(variant);
  }
  if (entry.preferredName) forms.push(nameTokens(entry.preferredName));
  return forms.filter(f => f.length > 0);
}

/**
 * Turning several signals into one score.
 *
 * The obvious thing — a weighted average — is wrong here, and the eval harness said so
 * within a minute of existing. These signals are not independent evidence to be pooled; they
 * are alternative *routes* to the same identification, and each one is silent on the cases
 * the others exist for. "cheung shin yee" is Xinyi Zhang: the syllable route recognises it
 * outright, and the phonetic route scores it near zero because not one letter of "cheung"
 * appears in "zhang". Averaging those two produced 0.42 — the right child, ranked first, and
 * then suppressed by the 0.45 floor. The teacher saw "nobody on this roster sounds like
 * that" for a child standing in front of them.
 *
 * So: the best route wins, capped by how far that route alone is trusted to go, and the
 * others can only *raise* the result by agreeing. A weak signal can never drag a strong one
 * under, which is the property the average lacked. Each weight now reads as "how sure this
 * route alone is allowed to make us" — initials at 0.15 can never carry a child on their own,
 * which is right, because half the roster shares initials with the other half.
 */
function combine(parts: readonly [number, number | null][], corroboration: number): number {
  const claims = parts
    .filter((p): p is [number, number] => p[1] !== null)
    .map(([cap, value]) => cap * value);
  if (claims.length === 0) return 0;

  const best = Math.max(...claims);
  const others = [...claims];
  others.splice(claims.indexOf(best), 1);
  const support = others.length > 0 ? others.reduce((a, b) => a + b, 0) / others.length : 0;

  // Corroboration closes some of the remaining gap to 1, never more. Two routes agreeing is
  // worth something; it is not worth as much as either route being certain.
  return Math.min(1, best + (1 - best) * corroboration * support);
}

/**
 * One child, scored against one transcript.
 *
 * The weighted average is over the signals this child actually has data for. See
 * `MatchableName` for why that matters.
 */
export function scoreEntry(
  spokenTokens: readonly string[],
  entry: MatchableName,
  config: MatchConfig = DEFAULT_CONFIG,
  now: number = Date.now(),
): { score: number; breakdown: ScoreBreakdown } {
  const empty: ScoreBreakdown = { syllable: 0, phonetic: 0, initials: 0, english: 0, recency: 0, alias: false };
  if (spokenTokens.length === 0) return { score: 0, breakdown: empty };

  // A transcript a teacher has already corrected onto this child is not evidence to weigh
  // against other evidence — it is a teacher telling us the answer. It short-circuits.
  if (config.layers.alias && entry.aliases?.length) {
    const said = spokenTokens.join(' ');
    if (entry.aliases.some(alias => nameTokens(alias).join(' ') === said)) {
      return { score: config.aliasScore, breakdown: { ...empty, alias: true } };
    }
  }

  const { layers, weights } = config;
  const reglue = layers.regluing;
  const forms = scorableNames(entry, config);

  const phonetic = Math.max(...forms.map(form => scoreName(spokenTokens, form, tokenSimilarity, reglue)));

  // Only asked when both sides have something Mandarin to say. See `isPinyinWord`.
  const bothPinyin =
    spokenTokens.some(token => couldBePinyin(token, layers)) &&
    forms.some(form => form.some(token => couldBePinyin(token, layers)));
  const syllable = layers.syllable && bothPinyin
    ? Math.max(...forms.map(form =>
        scoreName(spokenTokens, form, (a, b) => pinyinTokenSimilarity(a, b, layers), reglue)))
    : null;

  const initials = initialsSimilarity(nameInitials(spokenTokens), nameInitials(nameTokens(entry.name)));

  // Scored as an English word rather than as pinyin — that is the whole point of the signal.
  const english = entry.preferredName
    ? scoreName(spokenTokens, nameTokens(entry.preferredName), tokenSimilarity, reglue)
    : null;

  const recency = layers.recency ? recencyScore(entry.lastPickedAt, now, config.recencyHalfLifeDays) : null;

  const routes: [number, number | null][] = [
    [weights.syllable, syllable],
    // The letter route is trusted less wherever the syllable route had something to say.
    [syllable === null ? weights.phonetic : weights.phoneticWithSyllable, phonetic],
    [weights.initials, initials],
    [weights.english, english],
  ];

  // Recency is applied after the routes rather than as one of them, because it is not a
  // route: it says nothing about whether this transcript is this child's name. Folding it in
  // alongside the others made it actively harmful — a child with a recency score merely
  // *lower* than their other signals dragged the corroboration average down, so being picked
  // yesterday cost them 0.002. It is a nudge over whatever the routes concluded, sized as a
  // fraction of the headroom left to 1, so it can settle a tie and can never overturn a gap.
  const base = combine(routes, config.corroboration);
  const nudge = recency === null ? 0 : weights.recency * recency * (1 - base);

  return {
    score: Math.min(1, base + nudge),
    breakdown: {
      syllable: syllable ?? 0,
      phonetic,
      initials,
      english: english ?? 0,
      recency: recency ?? 0,
      alias: false,
    },
  };
}

/**
 * The same tokens with any single-letter word read as that letter's *name*.
 *
 * Returns null when nothing changed, so the caller can skip adding a duplicate hypothesis.
 * Applied before re-gluing on purpose: the whole point is that "leo" + "e" has to become
 * "leo" + "yi" while both are still separate words, so that gluing them produces "leoyi"
 * and finds Liuyi. After gluing, the letter is gone.
 */
function expandLetterNames(tokens: readonly string[]): string[] | null {
  let changed = false;
  const out = tokens.map(token => {
    const syllable = token.length === 1 ? LETTER_NAME_SYLLABLES[token] : undefined;
    if (!syllable) return token;
    changed = true;
    return syllable;
  });
  return changed ? out : null;
}

/**
 * Every child on the roster, scored against what was heard, best first.
 *
 * With several transcripts in, each child keeps their best reading across all of them. A
 * lower-confidence alternative is shaded down rather than discarded: the recogniser's own
 * ranking is worth something, but not much — its top hypothesis is the one that produced the
 * mangling in the first place.
 */
export function scoreRoster(
  spoken: SpokenInput,
  roster: readonly MatchableName[],
  config: MatchConfig = DEFAULT_CONFIG,
  now: number = Date.now(),
): NameScore[] {
  const candidates = asCandidates(spoken)
    .map(c => ({ tokens: spokenNameTokens(c.transcript), confidence: c.confidence ?? 1 }))
    .filter(c => c.tokens.length > 0)
    .flatMap(c => {
      // A spelled-out letter is another reading of the same utterance, so it enters as one
      // more hypothesis rather than replacing what was heard — "leo e" is read both as said
      // and as "leo yi", and whichever fits the roster wins. Shaded slightly, because the
      // literal reading is right whenever the letter really was a letter.
      const spelled = expandLetterNames(c.tokens);
      return spelled ? [c, { tokens: spelled, confidence: c.confidence * 0.95 }] : [c];
    });
  if (candidates.length === 0) return [];

  return roster
    .map(entry => {
      let best = { score: 0, breakdown: undefined as ScoreBreakdown | undefined };
      for (const candidate of candidates) {
        const scored = scoreEntry(candidate.tokens, entry, config, now);
        const shaded = scored.score * (0.9 + 0.1 * candidate.confidence);
        if (shaded > best.score) best = { score: shaded, breakdown: scored.breakdown };
      }
      return { key: entry.key, name: entry.name, score: best.score, breakdown: best.breakdown };
    })
    .sort((a, b) => b.score - a.score || a.name.localeCompare(b.name));
}

/**
 * Words that are plainly not part of a child's name.
 *
 * The panel asks for a name and nothing else, but people are not dictation machines: they
 * say "Emma Chen please", and a teacher used to the old paper list says "check in Emma".
 * Dropping these keeps that from dragging the score down — every spoken word the roster
 * cannot account for costs the right child a share of its score.
 *
 * Deliberately short, and deliberately not a command vocabulary. Nothing acts on these
 * words; they are only ignored.
 */
const NOT_A_NAME = new Set([
  'check', 'checked', 'checking', 'sign', 'signed', 'in', 'out', 'here', 'absent',
  'please', 'thanks', 'thank', 'okay', 'ok', 'um', 'uh', 'er', 'and', 'the',
  'a', 'is', 'was', 'for', 'now', 'next', 'student', 'kid', 'child',
]);

// "you" is deliberately absent from that list. It is a courtesy word in English and a
// perfectly good fragment of a romanised name — a recogniser hearing "Ziyuan" returns
// "zee you on", and dropping the middle of it costs the right child the match.

/** What was said, reduced to the words that could be part of a name. */
export function spokenNameTokens(spoken: string): string[] {
  const tokens = nameTokens(spoken).filter(token => !NOT_A_NAME.has(token));
  // If they said nothing else, take them at their word rather than matching on nothing.
  return tokens.length > 0 ? tokens : nameTokens(spoken);
}

/**
 * The children a spoken name might have been, best first — at most `MAX_SUGGESTIONS`.
 *
 * Empty when nothing on the roster comes close, which is a real answer: a name that belongs
 * to another class, or a recogniser that heard the playground instead of the teacher. Five
 * arbitrary children would be worse than saying so.
 */
export function suggestStudents(
  spoken: SpokenInput,
  roster: readonly MatchableName[],
  limit?: number,
  config: MatchConfig = DEFAULT_CONFIG,
  now: number = Date.now(),
): NameScore[] {
  const scored = scoreRoster(spoken, roster, config, now);
  const best = scored[0];
  if (!best || best.score < config.floor) return [];

  const cutoff = Math.max(config.floor, best.score - config.spread);
  return scored
    .filter(candidate => candidate.score >= cutoff)
    .slice(0, limit ?? config.maxSuggestions);
}

/**
 * Whether the leader is far enough ahead that a caller could act without asking.
 *
 * Nothing acts on this today, and that is a deliberate choice rather than an unfinished one:
 * the action at the end of this flow hands a child to an adult, and no confidence score
 * should be allowed to do that unattended. It is exported so the harness can report how
 * often a gap this size would in fact have been right — which is the evidence anyone should
 * want before wiring it to anything.
 */
export function isConfident(scored: readonly NameScore[], config: MatchConfig = DEFAULT_CONFIG): boolean {
  if (scored.length === 0 || scored[0].score < config.floor) return false;
  return scored.length === 1 || scored[0].score - scored[1].score >= config.autoSelectGap;
}
